import { useMemo, useState } from 'react';
import { Activity, RefreshCw, Copy } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { DescList, DescItem } from '../../components/DescList';
import {
  useApplicationHealth,
  useApplicationLogs,
  type Instance,
  type AppHealth,
} from '@/api/opennebula';

function healthTone(h: AppHealth): 'success' | 'danger' | 'warning' | 'muted' {
  if (h.healthy) return 'success';
  if (h.status && /heal|ok|run/i.test(h.status)) return 'success';
  if (h.status && /unheal|down|fail|error/i.test(h.status)) return 'danger';
  return h.status ? 'warning' : 'muted';
}

/**
 * Health status + container logs for a marketplace-app instance. Health polls
 * every 30s; logs every 15s. Both pause when the instance isn't powered on.
 */
export function ApplicationHealthLogs({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const { show } = useToast();
  const poweredOn = instance.status === 'POWERED_ON';
  const containers = useMemo(() => instance.metadata?.application?.template ?? [], [instance]);
  const [container, setContainer] = useState(containers[0] ?? 'n8n');
  const [lines, setLines] = useState(100);

  const { data: health, isLoading: hLoading } = useApplicationHealth(serviceId, instance.id, poweredOn);
  const { data: logs, isFetching: lFetching, refetch } = useApplicationLogs(
    serviceId,
    instance.id,
    { container, lines },
    poweredOn,
  );
  const h = health ?? {};

  const copyLogs = () => {
    if (logs?.logs) {
      void navigator.clipboard?.writeText(logs.logs);
      show({ tone: 'success', title: 'Logs copied' });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Activity size={16} /> Application health
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          {!poweredOn ? (
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>Power on the instance to monitor application health.</p>
          ) : hLoading ? (
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>Checking…</p>
          ) : (
            <DescList>
              <DescItem label="Status"><StatusPill tone={healthTone(h)}>{h.healthy ? 'Healthy' : h.status || 'Unknown'}</StatusPill></DescItem>
              {h.message && <DescItem label="Message">{h.message}</DescItem>}
              {h.endpoint && <DescItem label="Endpoint"><code style={{ fontSize: 'var(--vn-font-size-sm)', wordBreak: 'break-all' }}>{h.endpoint}</code></DescItem>}
              {h.http_status != null && <DescItem label="HTTP status">{h.http_status}</DescItem>}
              {h.response_time != null && <DescItem label="Response time">{h.response_time} ms</DescItem>}
            </DescList>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          actions={
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {containers.length > 1 && (
                <Select value={container} onValueChange={setContainer}>
                  <SelectTrigger style={{ width: 'min(160px, 100%)' }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {containers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={String(lines)} onValueChange={(v) => setLines(Number(v))}>
                <SelectTrigger style={{ width: 'min(110px, 100%)' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[100, 250, 500, 1000].map((n) => <SelectItem key={n} value={String(n)}>{n} lines</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="secondary" startIcon={<RefreshCw size={14} />} disabled={lFetching} onClick={() => refetch()}>Refresh</Button>
              <Button size="sm" variant="ghost" startIcon={<Copy size={14} />} disabled={!logs?.logs} onClick={copyLogs}>Copy</Button>
            </div>
          }
        >
          <CardTitle>Application logs</CardTitle>
        </CardHeader>
        <CardBody>
          {!poweredOn ? (
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>Power on the instance to view logs.</p>
          ) : logs?.error ? (
            <p style={{ margin: 0, color: 'var(--vn-danger-text)' }}>{logs.error}</p>
          ) : (
            <pre style={logBox}>{logs?.logs?.trim() || 'No log output.'}</pre>
          )}
        </CardBody>
      </Card>
    </>
  );
}

const logBox: React.CSSProperties = {
  margin: 0,
  maxHeight: 360,
  overflow: 'auto',
  // Recessed log "well" — real semantic tokens so it flips with the theme
  // (the previous --vn-bg-inset/--vn-fg-on-inset don't exist in @ui, so their
  // raw-hex fallbacks pinned it to a fixed dark that broke dark mode).
  background: 'var(--vn-bg-subtle)',
  color: 'var(--vn-fg-default)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-md)',
  padding: 'var(--vn-space-3)',
  fontSize: 'var(--vn-font-size-sm)',
  fontFamily: 'var(--vn-font-mono)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};
